
const lower = (v) => String(v ?? "").toLowerCase();

const isRegularBookingEvent = (event) => {
  if (!event) return false;
  // Skip Information overlays (sit in the "info" sub-lane).
  if (event.isInformationBooking) return false;
  if (lower(event.slot) === "info") return false;
  if (lower(event.cssClass).includes("is-information-booking")) return false;

  const type = lower(event.type);
  // Skip our own pending slots ("Available"), Google sync ("GoogleCalendar")
  // and Information events ("Information"). Substring match is intentional:
  // it tolerates casing drift and "Available_*" / "GoogleCalendar_*" variants.
  if (type.includes("information")) return false;
  if (type.includes("google")) return false;
  if (event.isGoogleCalendar) return false;
  if (type.includes("available")) return false;
  // Anything else (Booking / Reservation / Maintenance / unknown custom types)
  // counts as a real booking that an admin is overlapping -> force Information.
  return true;
};

const intersectsHalfOpen = (aStart, aEnd, bStart, bEnd) => {
  return aStart < bEnd && aEnd > bStart;
};

// Resource match is tolerant: two ids are considered the same lane when one
// string contains the other (case-insensitive). This shields us from any
// "grp_" / "google_calendar_" prefixing we may get from one side but not the
// other, while UUIDs stay unique enough to avoid false positives.
const resourceMatches = (evt, pending) => {
  const target = lower(pending?.resource);
  if (!target) return false;
  const candidates = [evt?.resource, evt?.inventoryId, evt?.equipment?.inventoryId]
    .map(lower)
    .filter(Boolean);
  return candidates.some((c) => c === target || c.includes(target) || target.includes(c));
};

/**
 * @param {Array<{start:string,end:string,resource:string|number}>} pendingReservations
 * @param {Array<object> | null | undefined} allEvents
 *        Full list of events currently fed to Mobiscroll (the `eventData` prop).
 *        We read from the source list instead of calling
 *        `calendarInstance.getEvents(start, end)` because Mobiscroll may trim
 *        its internal store to the visible viewport, which made the previous
 *        implementation silently miss overlaps on the deployed environment.
 * @returns {boolean} true if at least one pendingReservation overlaps a real booking.
 */
export const detectOverlapWithRegularBookings = (pendingReservations, allEvents) => {
  if (!pendingReservations?.length || !Array.isArray(allEvents) || allEvents.length === 0) {
    return false;
  }

  return pendingReservations.some((pending) => {
    const pStart = new Date(pending.start).getTime();
    const pEnd = new Date(pending.end).getTime();
    if (Number.isNaN(pStart) || Number.isNaN(pEnd)) return false;

    return allEvents.some((evt) => {
      if (!isRegularBookingEvent(evt)) return false;
      if (!resourceMatches(evt, pending)) return false;
      const eStart = new Date(evt.start).getTime();
      const eEnd = new Date(evt.end).getTime();
      if (Number.isNaN(eStart) || Number.isNaN(eEnd)) return false;
      return intersectsHalfOpen(pStart, pEnd, eStart, eEnd);
    });
  });
};

/**
 * Detects whether any two pending reservations overlap each other on the same
 * resource. Used to force Information type when the admin drag-creates several
 * slots that collide -- BE rejects duplicate Run/Action bookings on the same
 * timeframe, so the only way to persist the whole wizard pass is to flip the
 * booking type to Information (which BE allows to overlap freely).
 *
 * @param {Array<{start:string,end:string,resource:string|number}>} pendingReservations
 * @returns {boolean} true if at least one pair of pending slots overlaps.
 */
export const detectOverlapWithinPending = (pendingReservations) => {
  if (!Array.isArray(pendingReservations) || pendingReservations.length < 2) {
    return false;
  }

  // O(n^2) is fine -- the wizard list is admin-drag-created, single digits.
  for (let i = 0; i < pendingReservations.length; i++) {
    const a = pendingReservations[i];
    const aStart = new Date(a?.start).getTime();
    const aEnd = new Date(a?.end).getTime();
    if (Number.isNaN(aStart) || Number.isNaN(aEnd)) continue;

    for (let j = i + 1; j < pendingReservations.length; j++) {
      const b = pendingReservations[j];
      if (!resourceMatches(b, a)) continue;
      const bStart = new Date(b?.start).getTime();
      const bEnd = new Date(b?.end).getTime();
      if (Number.isNaN(bStart) || Number.isNaN(bEnd)) continue;
      if (intersectsHalfOpen(aStart, aEnd, bStart, bEnd)) return true;
    }
  }
  return false;
};
